import {
  cleanMultipleArticles,
  cleanStoreArticles,
  createLogisticFile,
  destroyStore,
  downloadStockAuditCsv,
  fetchStoreCommandNumber,
  mooveArticlesBetweenStores,
  updateConfigDoor,
  useConfigDoor,
  useLegalEntities,
  useStoreProductArticles,
  useStoreStock,
} from '@gozoki/api'
import {
  OPENING_MODE_VALUES,
  type DayOfWeek,
  type OpeningConfigLine,
  type StockEntry,
  type Store,
  type UniqueArticle,
} from '@gozoki/api-types'
import type { LogisticFileForm } from '@gozoki/api/src/forms/useLogisticFileForm'
import { frenchFormat, getCountryLabel, includesLowerCase, priceToString } from '@gozoki/tools'
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers'
import { captureException } from '@sentry/react'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, Download } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '~/lib/tuyau'
import z from 'zod/v3'
import { withRoutePermissions } from '~/utils/rbac'
import StoreCSVExportButton from '../../../components/buttons/StoreCSVExportButton'
import StoreRestockCSVExportButton from '../../../components/buttons/StoreRestockCSVExportButton'
import { ConfirmButton } from '../../../components/communication/ConfirmButton'
import { ConfirmResourceDeletionButton } from '../../../components/communication/ConfirmDeletionButton'
import StockCard from '../../../components/communication/StockCard'
import { useToast } from '../../../components/communication/Toast'
import DivTabPanel from '../../../components/containers/DivTabPanel'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import TabPanel from '../../../components/containers/TabPanel'
import Thumbnail from '../../../components/icons/Thumbnail'
import Page from '../../../components/Page'
import { KiosksManagementPanel } from '../../../components/routes/stores/config/KiosksManagementPanel'
import { OpeningHoursConfigPanel } from '../../../components/routes/stores/config/OpeningHoursConfigPanel'
import { InventoriesStatsTab } from '../../../components/routes/stores/inventories/InventoriesStatsTab'
import { PastInventoriesTab } from '../../../components/routes/stores/inventories/PastInventoriesTab'
import {
  PendingInventoryTab,
  type PendingInventoryData,
} from '../../../components/routes/stores/inventories/PendingInventoryTab'
import { EstablishmentPicker } from '../../../utils/dashboard/establishmentPicker'
import { SellerPicker } from '../../../utils/dashboard/sellerPicker'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { useRights } from '../../../utils/hooks'
import { generatePDF, getMinStock } from '../../../utils/pdf/writeCommandPdfFile'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides, DataGridOverrides } from '../../../utils/theme/overrides.theme'
const tabs = ['stocks', 'details', 'inventory', 'inventories', 'hours', 'kiosks', 'stats'] as const

export const Route = createFileRoute('/_authenticated/stores/$storeId/')({
  staticData: {
    permission: ['read', 'stores'],
  },
  component: StoreDetailsPage,
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
  validateSearch: z.object({ tab: z.enum(tabs).optional() }),
})
withRoutePermissions(Route)

export interface CommandModeChoice {
  value: 'last' | 'none' | 'custom'
  title: string
}

const lastCommandModeChoices = [
  {
    title: 'Relier au dernier bon de commande généré',
    value: 'last',
  },
  {
    title: 'Ne pas relier à un bon de commande, la livraison ne sera pas enregistrée',
    value: 'none',
  },
  {
    title: 'Utiliser un autre bon de commande',
    value: 'custom',
  },
]

const stockColDef: GridColDef[] = [
  {
    field: 'imageUrl',
    headerName: 'Image',
    headerAlign: 'center', // Align the header text to center
    width: 80,
    renderCell: (field) => <Thumbnail url={field.value} alt="product image" size="60px" />,
  },
  {
    field: 'reference',
    headerName: 'Réf',
    width: 100,
  },
  {
    field: 'label',
    headerName: 'Libellé',
    width: 150,
  },
  {
    field: 'stock',
    headerName: 'Stock total',
    width: 50,
  },
  {
    field: 'totalValue',
    headerName: 'Valorisation totale',
    renderCell: (field) => (field.value === 0 ? '0 €' : `${frenchFormat(field.value)}\u00A0€`),
    width: 100,
  },
  {
    field: 'agStock',
    headerName: 'Stock Anti-Gaspi',
    width: 50,
  },
  {
    field: 'totalValueAntiGaspi',
    headerName: 'Valorisation Anti-Gaspi',
    renderCell: (field) => (field.value === 0 ? '0 €' : `${frenchFormat(field.value)}\u00A0€`),
    width: 130,
  },
  {
    field: 'expiredStock',
    headerName: 'Stock expiré',
    width: 50,
  },
  {
    field: 'totalValueExpired',
    headerName: 'Valeur expirée',
    renderCell: (field) => (field.value === 0 ? '0 €' : `${frenchFormat(field.value)}\u00A0€`),
    width: 100,
  },

  {
    field: 'buyingPrice',
    headerName: "Prix d'achat",
    width: 100,
  },
  {
    field: 'colisage',
    headerName: 'Colisage',
    width: 100,
  },

  {
    field: 'currentReaproNeeds',
    headerName: "Besoin de réassort pour aujourd'hui",
    renderCell: (field) => (field.value === 0 ? ' - ' : field.value),
    width: 100,
  },
  {
    field: 'establishment',
    headerName: 'Vendeur',
    renderCell: (field) => field.value?.seller?.label ?? ' - ',
    width: 100,
  },
]

type ArticleState = 'antigaspi' | 'normal' | 'expired'

const getArticleState = (article: UniqueArticle): ArticleState => {
  if (article.isExpired) {
    return 'expired'
  }
  if (article.isAntiGaspi) {
    return 'antigaspi'
  }

  return 'normal'
}

const ARTICLE_STATE_LABEL: { [key in ArticleState]: string } = {
  antigaspi: 'Anti-gaspi',
  expired: 'Expiré',
  normal: 'Normal',
}

type FilterArticleState = ArticleState | 'all'

const articleStateFilter = (article: UniqueArticle, state: FilterArticleState) => {
  if (state === 'all') {
    return true
  }

  return getArticleState(article) === state
}

const getChipCss = (state: ArticleState) => {
  const chipCss = {
    backgroundColor: ThemeColors.NORMAL_GREEN as string,
    color: ThemeColors.WHITE,
  }

  if (state === 'expired') {
    chipCss.backgroundColor = ThemeColors.RED
  }

  if (state === 'antigaspi') {
    chipCss.backgroundColor = ThemeColors.ANTI_GASPI_ORANGE
  }

  return chipCss
}

const pad = (n: number) => n.toString().padStart(2, '0')

const articlesColDef: GridColDef[] = [
  {
    field: 'reference',
    headerName: 'Réf',
    width: 180,
  },
  {
    field: 'label',
    flex: 1,
    headerName: 'Libellé',
  },
  {
    field: 'dlc',
    flex: 1,
    headerName: 'DLC',
  },
  {
    field: 'state',
    flex: 1,
    headerName: 'Etat',
    renderCell: (field) => (
      <Chip label={ARTICLE_STATE_LABEL[field.value as ArticleState]} sx={getChipCss(field.value)} />
    ),
  },
]

const productLabelFromStockEntries = (stockEntries: StockEntry[], id: number) => {
  const stockEntry = stockEntries.find((entry) => entry.id === id)
  if (stockEntry === undefined) {
    return ''
  }

  return stockEntry.label
}

function StoreDetailsPage() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useParams()
  const { tab: rawTab } = Route.useSearch()

  const [Toast, showToast] = useToast()

  const isStockAdmin = useRights(['STOCK_ADMIN'])
  const isStockReader = useRights(['STOCK_READER'])
  const isSuperAdminUser = useRights(['SUPER_ADMIN'])

  const visibleTabs: readonly (typeof tabs)[number][] = isStockReader
    ? tabs
    : tabs.filter((t) => t === 'hours' || t === 'kiosks')
  const tab = rawTab ?? (isStockReader ? 'stocks' : 'hours')
  const tabIndex = visibleTabs.indexOf(tab)

  // Tuyau infers the raw Lucid model type; the wire payload is the serialized shape (api-types Store).
  const { data: rawStore, isLoading } = useQuery(
    api.stores.show.queryOptions(storeId ? { params: { id: storeId } } : skipToken),
  )
  const data = rawStore as Store | undefined

  const [modalMode, setModalMode] = useState<'command' | 'moove' | undefined>(undefined)
  const [pendingInventoryData, setPendingInventoryData] = useState<PendingInventoryData | null>(
    null,
  )

  const { data: legalEntities = [] } = useLegalEntities()

  const currentEntity =
    legalEntities?.find((entity) => entity.id === data?.legalEntityId)?.name ?? data?.legalEntityId

  // ****************************************************** //
  //                      STOCK VARIABLES                   //
  // ****************************************************** //

  const [stockSearch, setStockSearch] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<number | undefined>(undefined)
  const [selectedArticles, setSelectedArticles] = useState<number[]>([])

  const [isInitializedDoorConfig, setIsInitializedDoorConfig] = useState(false)

  const [articleState, setArticleState] = useState<FilterArticleState>('all')

  const [openingConfigs, setOpeningConfigs] = useState<{ [key: number]: OpeningConfigLine[] }>({
    1: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    2: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    3: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    4: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    5: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    6: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
    7: [
      {
        beginTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        endTime: DateTime.now().set({ hour: 0, minute: 0 }).toString(),
        openingMode: OPENING_MODE_VALUES[0],
      },
    ],
  })

  // ****************************************************** //
  //                          QUERIES                       //
  // ****************************************************** //

  const stock = useStoreStock(storeId)
  const articles = useStoreProductArticles(storeId, selectedProduct ?? Number.NaN)

  // ****************************************************** //
  //           QUERY TO DATAGRID TRANSFORMATION             //
  // ****************************************************** //

  const stockRows = useMemo(() => {
    if (stock.data === undefined) {
      return []
    }
    return stock.data.rows.filter((row) => includesLowerCase(row.label, stockSearch))
  }, [stock.data, stockSearch])

  const articlesRows = useMemo(() => {
    if (articles.data === undefined) {
      return []
    }
    return articles.data

      .filter((a) => articleStateFilter(a, articleState))

      .map((article) => ({
        dlc: article.displayedDlc,
        id: article.id,
        label: article.product.label,
        reference: article.product.reference,
        state: getArticleState(article),
      }))
  }, [articles.data, articleState])

  const { data: doorBaseConfiguration } = useConfigDoor(Number(storeId))

  // ****************************************************** //
  //                         MUTATIONS                      //
  // ****************************************************** //

  const deleteStoreMut = useMutation({
    mutationFn: destroyStore,
    onSuccess: () => {
      navigate({ to: '/stores' })
    },
  })

  const cleanStoreArticlesMut = useMutation({
    mutationFn: cleanStoreArticles,
    onSuccess: () => {
      stock.refetch()
    },
  })

  const cleanArticlesMut = useMutation({
    mutationFn: cleanMultipleArticles,
    onSuccess: () => {
      stock.refetch()
      setSelectedArticles([])
      setSelectedProduct(undefined)
    },
  })

  const { mutate: mutateUpdateConfigDoor } = useMutation({
    mutationFn: async () => updateConfigDoor(storeId, openingConfigs),
    onError: (error) => {
      showToast({
        message: getErrorMessageWithSentry(error) || 'une erreur est survenue',
        severity: 'error',
      })
    },
    onSuccess: (response) => {
      showToast({
        message: response.message,
        severity: 'success',
      })
    },
  })

  // ****************************************************** //
  //                         HANDLERS                       //
  // ****************************************************** //

  const handleChange = (newValue: number) => {
    const newTab = visibleTabs.at(newValue) ?? visibleTabs[0]

    navigate({ search: { tab: newTab } })
  }

  const navigateBack = () => {
    if (selectedProduct !== undefined) {
      setSelectedProduct(undefined)
    } else {
      navigate({ to: '/stores' })
    }
  }

  const updateStore = () => navigate({ to: '/stores/new', search: { storeId } })

  // ****************************************************** //
  //                          BDL                           //
  // ****************************************************** //

  const today = DateTime.now()
  const twoDaysLater = today.plus({ days: 2 })
  const [showModal, setShowModal] = useState(false)

  const [dateDepart, setDateDepart] = useState<DateTime>(today)
  const [dateLivraison, setDateLivraison] = useState<DateTime>(twoDaysLater)

  const [isLoadingBdl, setIsLoadingBdl] = useState(false)

  const [currentReceivingStore, setCurrentReceivingStore] = useState<number>(storeId)
  const [currentShippingStore, setCurrentShippingStore] = useState<number>(storeId)

  const [currentSeller, setCurrentSeller] = useState<number>(0)
  const [currentEstablishment, setCurrentEstablishment] = useState<number>(0)

  const handleChangeSeller = useCallback((newSeller: number) => {
    setCurrentSeller(newSeller)
    setCurrentEstablishment(0)
  }, [])

  const { data: rawReceivingStore } = useQuery(
    api.stores.show.queryOptions(
      currentReceivingStore ? { params: { id: currentReceivingStore } } : skipToken,
    ),
  )
  const { data: rawShippingStore } = useQuery(
    api.stores.show.queryOptions(
      currentShippingStore ? { params: { id: currentShippingStore } } : skipToken,
    ),
  )
  const receivingStore = rawReceivingStore as Store | undefined
  const shippingStore = rawShippingStore as Store | undefined

  const [linkableCommandReference, setLinkableCommandReference] = useState<string | null>(null)
  const [lastCommandMode, setLastCommandMode] = useState<CommandModeChoice['value']>('last')
  const [boxQuantity, setBoxQuantity] = useState<number>()
  const { mutateAsync: generateLigisticFile } = useMutation({
    mutationFn: createLogisticFile,
    onError: (err) => {
      captureException(err)
      showToast({
        message: 'Erreur lors de la génération du bon de livraison',
        severity: 'error',
      })
    },
    onSuccess: () => {
      showToast({
        message: 'Bon de livraison généré',
        severity: 'success',
      })
    },
  })

  /** Find the store references that have UniqueArticle with warehouseIn set */
  const warehousesIn = useMemo(() => {
    if (!stockRows) {
      return []
    }
    return [...new Set(stockRows.map((row) => row.warehouseIn))].filter((row) => row !== '')
  }, [stockRows])

  const handleGenerateCommand = useCallback(async () => {
    setIsLoadingBdl(true)
    if (!data) {
      return
    }
    const commandNumber = await fetchStoreCommandNumber(data.id)
    const commandReference = `${'BCL'}-${data.id}-${commandNumber}`
    const deliveryDate = dateLivraison.weekdayLong?.toLowerCase() as DayOfWeek

    const reaproNeeds = stockRows.filter((row) => {
      const minStock = getMinStock(row.dailyMinStocksMap, deliveryDate) ?? 0
      const needReapro = row.stock - row.expiredOrWillExpireStock < minStock
      const sellerMatch = row.sellerId === currentSeller || currentSeller === 0
      const establishmentMatch =
        row.establishmentId === currentEstablishment || currentEstablishment === 0
      return sellerMatch && establishmentMatch && needReapro
    })

    const lines = reaproNeeds.map((row) => {
      const minStock = getMinStock(row.dailyMinStocksMap, deliveryDate) ?? 0
      const quantityNeed = minStock - row.stock + row.expiredOrWillExpireStock
      return {
        id: row.id,
        quantity: quantityNeed,
      }
    })

    const logisticFileData: LogisticFileForm = {
      commandReference: null,
      lines,
      logisticFileType: 'command',
      receivingDate: dateLivraison.toISODate() ?? '',
      receivingStoreId: data.id,
      reference: commandReference,
      shippingDate: today.toISODate() ?? '',
      shippingStoreId: null,
      useLastCommand: lastCommandMode === 'last',
    }
    await generateLigisticFile(logisticFileData)
      .then(async () => {
        await generatePDF(
          reaproNeeds,
          data,
          undefined,
          today,
          dateLivraison,
          'command',
          commandReference,
        )
        setIsLoadingBdl(false)
        setShowModal(false)
        setCurrentEstablishment(0)
        setCurrentSeller(0)
      })
      .catch(() => {
        setIsLoadingBdl(false)
        setCurrentEstablishment(0)
        setCurrentSeller(0)
      })
  }, [
    data,
    dateLivraison,
    generateLigisticFile,
    stockRows,
    today,
    currentEstablishment,
    currentSeller,
    lastCommandMode,
  ])

  const handleGenerateDelivery = useCallback(async () => {
    const reaproNeeds =
      currentShippingStore === 0
        ? stockRows.filter((row) => row.stock > 0)
        : stockRows.filter((row) => {
            const hasNeeds = row.stock > 0
            const warhouseMatch =
              row.warehouseIn !== '' && row.warehouseIn === shippingStore?.checkpointStoreRef
            const sellerMatch = row.sellerId === currentSeller || currentSeller === 0
            const establishmentMatch =
              row.establishmentId === currentEstablishment || currentEstablishment === 0
            return hasNeeds && warhouseMatch && sellerMatch && establishmentMatch
          })

    if (!data) {
      return
    }
    setIsLoadingBdl(true)
    const commandNumber = await fetchStoreCommandNumber(receivingStore?.id ?? data.id)
    const commandReference = `BDL-${data.id}-${commandNumber}`
    const lines = reaproNeeds.map((row) => ({
      id: row.id,
      quantity: row.stock,
    }))

    const logisticFileData: LogisticFileForm = {
      commandReference: lastCommandMode === 'custom' ? linkableCommandReference : null,
      lines,
      logisticFileType: 'shipping',
      receivingDate: dateLivraison.toISODate() ?? '',
      receivingStoreId: receivingStore?.id ?? data.id,
      reference: commandReference,
      shippingDate: dateDepart.toISODate() ?? '',
      shippingStoreId: shippingStore?.id ?? null,
      useLastCommand: lastCommandMode === 'last',
    }

    await generateLigisticFile(logisticFileData)
      .then(async () => {
        await generatePDF(
          reaproNeeds,
          receivingStore ?? data,
          shippingStore ?? undefined,
          today,
          dateLivraison,
          'delivery',
          commandReference,
          boxQuantity,
        )
        setIsLoadingBdl(false)
        setShowModal(false)
        setCurrentEstablishment(0)
        setCurrentSeller(0)
        setBoxQuantity(undefined)
      })
      .catch(() => {
        setIsLoadingBdl(false)
        setCurrentEstablishment(0)
        setCurrentSeller(0)
        setBoxQuantity(undefined)
      })
  }, [
    receivingStore,
    stockRows,
    data,
    shippingStore,
    dateDepart,
    dateLivraison,
    lastCommandMode,
    linkableCommandReference,
    generateLigisticFile,
    today,
    currentShippingStore,
    currentEstablishment,
    currentSeller,
    boxQuantity,
  ])

  // ****************************************************** //
  //                       STOCK AUDIT                      //
  // ****************************************************** //

  const { mutate: downloadAuditFile, isPending: loadingAudit } = useMutation({
    mutationFn: downloadStockAuditCsv,
    onError: (err) => {
      console.error(err)
      captureException(err)
      showToast({
        message: 'Erreur lors du téléchargement du fichier',
        severity: 'error',
      })
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const now = new Date()

      const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate(),
      )}_${pad(now.getHours())}h${pad(now.getMinutes())}`
      link.setAttribute('download', `stock-audit_${dateStr}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    },
  })

  const handleDownloadAuditFile = useCallback(() => {
    downloadAuditFile(storeId)
  }, [downloadAuditFile, storeId])

  // ****************************************************** //
  //                      Moove articles                    //
  // ****************************************************** //

  const { mutate: mooveArticles } = useMutation({
    mutationFn: mooveArticlesBetweenStores,
    onError: (error) => {
      captureException(error)
      showToast({
        message: 'Erreur lors du déplacement des articles',
        severity: 'error',
      })
    },
    onSuccess: async () => {
      await stock.refetch()
      setModalMode(undefined)
      setShowModal(false)
    },
  })

  // ****************************************************** //
  //                  GLOBAL VALIDATE                       //
  // ****************************************************** //

  const handleMooveOrDelete = useCallback(() => {
    if (modalMode === 'command') {
      if (data?.storeType === 'BOUTIQUE' || data?.storeType === 'WAREHOUSE') {
        handleGenerateCommand()
      } else {
        handleGenerateDelivery()
      }
    } else if (modalMode === 'moove') {
      if (!selectedProduct) {
        mooveArticles({
          allArticles: true,
          destinationId: currentReceivingStore,
          originId: storeId,
        })
      } else if (selectedArticles.length > 0) {
        mooveArticles({
          articlesIds: selectedArticles,
          destinationId: currentReceivingStore,
          originId: storeId,
        })
      } else {
        showToast({
          message: 'Aucun article sélectionné',
          severity: 'error',
        })
      }
    }
  }, [
    currentReceivingStore,
    data?.storeType,
    handleGenerateCommand,
    handleGenerateDelivery,
    modalMode,
    mooveArticles,
    selectedArticles,
    selectedProduct,
    showToast,
    storeId,
  ])

  useEffect(() => {
    if (doorBaseConfiguration && !isInitializedDoorConfig) {
      const convertedConfigs: { [key: number]: OpeningConfigLine[] } = Object.fromEntries(
        Object.entries(doorBaseConfiguration).map(([day, configLines]) => {
          const convertedLines = configLines.map(
            ({ openingMode, beginTime, preAuthorizationAmount }, index, array) => ({
              openingMode,
              beginTime: DateTime.fromFormat(beginTime, 'HH:mm').toString(),
              endTime:
                index < array.length - 1
                  ? DateTime.fromFormat(array[index + 1].beginTime, 'HH:mm').toString()
                  : DateTime.fromObject({ hour: 0, minute: 0 }).toString(),
              ...(preAuthorizationAmount !== undefined ? { preAuthorizationAmount } : {}),
            }),
          )
          return [Number(day), convertedLines]
        }),
      )

      setOpeningConfigs(convertedConfigs)
      setIsInitializedDoorConfig(true)
    }
  }, [doorBaseConfiguration, isInitializedDoorConfig])

  const handleDayConfigChange = useCallback(
    (dayNumber: number, configCards: OpeningConfigLine[]) => {
      setOpeningConfigs((prev) => ({
        ...prev,
        [dayNumber]: configCards,
      }))
    },
    [],
  )

  return (
    <>
      <Toast />
      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 500,
          }}
        >
          {modalMode ? (
            <Typography
              style={fonts.pageSubtitle}
              sx={{ marginBottom: 2, marginTop: 2, textAlign: 'center' }}
            >
              {modalMode === 'command'
                ? `Générer un bon de ${data?.storeType === 'BOUTIQUE' ? 'commande' : 'livraison'}`
                : 'Sélectionner la destination'}
            </Typography>
          ) : null}
          {modalMode === 'command' && data?.storeType === 'SHIPPING' ? (
            <div style={{ minWidth: 310, padding: 20 }}>
              <Typography sx={{ paddingBottom: 1 }}>Date de départ</Typography>
              <DatePicker
                value={dateDepart}
                onChange={(date: DateTime | null) => setDateDepart(date ?? today)}
                format="dd/MM/yyyy"
              />
            </div>
          ) : null}
          {modalMode === 'command' ? (
            <div style={{ minWidth: 310, padding: 20 }}>
              <div style={{ paddingBottom: 20 }}>
                <Typography sx={{ paddingBottom: 1 }}>
                  {`Date de Livraison
                            ${data?.storeType === 'BOUTIQUE' ? '  souhaitée' : ''}`}
                </Typography>
                <DatePicker
                  value={dateLivraison}
                  onChange={(date: DateTime | null) => setDateLivraison(date ?? today)}
                  format="dd/MM/yyyy"
                />
              </div>
              <div>
                <div style={{ flex: 1, paddingBottom: 10 }}>
                  <Typography sx={{ paddingBottom: 1 }}>Vendeur</Typography>
                  <SellerPicker setCurrentSeller={handleChangeSeller} />
                </div>
                {Boolean(currentSeller) && (
                  <div style={{ flex: 1 }}>
                    <Typography sx={{ paddingBottom: 1 }}>Établissement</Typography>
                    <EstablishmentPicker
                      setCurrentEstablishment={setCurrentEstablishment}
                      defaultEstablishment={0}
                      currentSellerId={currentSeller}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}
          {modalMode === 'command' && data?.storeType === 'SHIPPING' ? (
            <div style={{ minWidth: 310, padding: 20 }}>
              <Typography sx={{ paddingBottom: 2 }}>Plateforme de départ</Typography>
              <StoresPicker
                onlyPlatforms
                setUniqueStore={setCurrentShippingStore}
                storesToKeep={warehousesIn}
                allowAllStores
              />
            </div>
          ) : null}
          {modalMode === 'moove' || (modalMode === 'command' && data?.storeType === 'SHIPPING') ? (
            <div style={{ minWidth: 310, padding: 20 }}>
              <Typography sx={{ paddingBottom: 2 }}>
                {modalMode === 'command' ? 'Boutique à livrer' : 'Destination'}
              </Typography>
              <StoresPicker
                onlyBoutiques={modalMode === 'command'}
                setUniqueStore={setCurrentReceivingStore}
                defaultStore={data?.storeType === 'SHIPPING' ? storeId : undefined}
              />
            </div>
          ) : null}
          {modalMode === 'command' && data?.storeType === 'SHIPPING' ? (
            <div style={{ padding: 20 }}>
              <FormControl fullWidth>
                <InputLabel>Bon de commande associé</InputLabel>
                <Select
                  label="Bon de commande associé"
                  defaultValue={lastCommandModeChoices[0].value}
                  onChange={(e) => {
                    setLastCommandMode(e.target.value as CommandModeChoice['value'])
                  }}
                  style={{ minWidth: 300 }}
                >
                  {lastCommandModeChoices?.map((commandChoice) => (
                    <MenuItem key={commandChoice.value} value={commandChoice.value}>
                      {commandChoice.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : null}

          {modalMode === 'command' &&
          data?.storeType === 'SHIPPING' &&
          lastCommandMode === 'custom' ? (
            <div style={{ padding: 20 }}>
              <Typography>Numéro du bon de commande</Typography>
              <TextField
                onChange={(e) => {
                  setLinkableCommandReference(e.target.value)
                }}
                value={linkableCommandReference}
              />
            </div>
          ) : null}

          {modalMode === 'command' && data?.storeType === 'SHIPPING' && (
            <div style={{ padding: 20 }}>
              <Typography>Nombre de caisses</Typography>
              <TextField
                onChange={(e) => {
                  if (Number.isNaN(e.target.value)) {
                    setBoxQuantity(undefined)
                  } else {
                    setBoxQuantity(Number.parseInt(e.target.value, 10))
                  }
                }}
                value={boxQuantity}
                type="number"
              />
            </div>
          )}

          <div style={{ padding: 20 }}>
            <Button
              variant="contained"
              style={{ minWidth: 300 }}
              onClick={handleMooveOrDelete}
              disabled={isLoadingBdl}
            >
              Valider
            </Button>
          </div>
        </div>
      </Dialog>
      <Page>
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <IconButton onClick={navigateBack}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography
            style={{ ...fonts.pageSubtitle, marginLeft: '16px', marginBottom: '16px' }}
            flexGrow={1}
          >
            {`${data?.label}${
              selectedProduct === undefined
                ? ''
                : ` / ${productLabelFromStockEntries(stockRows, selectedProduct)}`
            }`}
          </Typography>
          {tab === 'details' && (
            <>
              <ConfirmResourceDeletionButton
                handleResourceDeletion={() => deleteStoreMut.mutateAsync(storeId)}
                disabled={deleteStoreMut.isPending}
              />
              <Button variant="contained" onClick={updateStore}>
                Modifier
              </Button>
            </>
          )}
          {tab === 'inventory' && pendingInventoryData && isSuperAdminUser && (
            <>
              <Typography
                sx={{
                  marginRight: 3,
                }}
              >
                Validation Admin :
              </Typography>
              <Button
                variant="outlined"
                color="error"
                sx={{ bgcolor: 'white', height: '36px', marginRight: '8px' }}
                onClick={pendingInventoryData.handleCancel}
                disabled={pendingInventoryData.isPending}
              >
                Annuler l'inventaire
              </Button>
              <Button
                variant="contained"
                sx={{ height: '36px' }}
                startIcon={
                  pendingInventoryData.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Check />
                  )
                }
                onClick={pendingInventoryData.handleValidate}
                disabled={pendingInventoryData.isPending}
              >
                {pendingInventoryData.isPending ? 'Validation…' : "Valider l'inventaire"}
              </Button>
            </>
          )}
          {tab === 'hours' && (
            <>
              <Button
                sx={{ marginRight: '8px' }}
                variant="outlined"
                onClick={() => setIsInitializedDoorConfig(false)}
              >
                Annuler
              </Button>

              <Button
                sx={{ marginLeft: '8px' }}
                variant="contained"
                onClick={() => mutateUpdateConfigDoor()}
              >
                Enregistrer
              </Button>
            </>
          )}
        </div>

        <div style={{ display: 'flex' }}>
          <Tabs value={tabIndex} onChange={(_, v) => handleChange(v)} sx={{ marginBottom: '16px' }}>
            {visibleTabs.includes('stocks') && <Tab label="Stocks" />}
            {visibleTabs.includes('details') && <Tab label="Informations" />}
            {visibleTabs.includes('inventory') && <Tab label="Inventaire en cours" />}
            {visibleTabs.includes('inventories') && <Tab label="Tous les inventaires" />}
            {visibleTabs.includes('hours') && <Tab label="Gestion des horaires" />}
            {visibleTabs.includes('kiosks') && <Tab label="Gestion des kiosks" />}
            {visibleTabs.includes('stats') && <Tab label="Statistiques" />}
          </Tabs>
          <div style={{ flex: 1 }} />

          {tab === 'details' &&
          selectedArticles.length > 0 &&
          isStockAdmin &&
          data?.storeType !== 'SHIPPING' ? (
            <>
              <ConfirmButton
                buttonText="Supprimer les articles sélectionnés"
                buttonProps={{
                  disabled: cleanStoreArticlesMut.isPending,
                  sx: {
                    marginRight: '16px',
                    height: '42px',
                    ...ButtonOverrides.redOutlined,
                  },
                  variant: 'outlined',
                }}
                description="Etes-vous sur de vouloir supprimer les articles sélectionnés ? Cette action est irréversible et bloquera toutes les ventes jusqu'au prochain inventaire"
                onConfirm={() => cleanArticlesMut.mutateAsync(selectedArticles)}
                title="Supprimer tous les articles ?"
                confirmButton={
                  cleanStoreArticlesMut.isPending
                    ? {
                        loading: true,
                        text: 'Suppression en cours     ',
                      }
                    : { text: 'Supprimer' }
                }
              />

              <Button
                variant="contained"
                onClick={() => {
                  setModalMode('moove')
                  setShowModal(true)
                }}
              >
                Déplacer les articles sélectionnés
              </Button>
            </>
          ) : null}
        </div>

        <TabPanel value={tab} index="details" sx={{ margin: 'auto', width: '50vw' }}>
          <LoadingPaper sx={{ padding: '24px', paddingBottom: '40px' }} loading={isLoading}>
            <Typography mb="8px" style={fonts.inputHeader}>
              Référence
            </Typography>
            <TextField disabled fullWidth value={data?.checkpointStoreRef} />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Nom
            </Typography>
            <TextField disabled fullWidth value={data?.label} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Nom court
            </Typography>
            <TextField disabled fullWidth value={data?.shortLabel ?? ''} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Adresse
            </Typography>
            <TextField disabled fullWidth value={data?.address.address} />

            {data?.address.addressAdditionnal && (
              <>
                <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                  Adresse additionnelle
                </Typography>
                <TextField disabled fullWidth value={data?.address.addressAdditionnal} />
              </>
            )}

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Ville
            </Typography>
            <TextField disabled fullWidth value={data?.address.city} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Code postal
            </Typography>
            <TextField disabled fullWidth value={data?.address.postalCode} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Pays
            </Typography>
            <TextField
              disabled
              fullWidth
              value={data?.address.country ? getCountryLabel(data.address.country) : ''}
            />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Référence Luna
            </Typography>
            <TextField disabled fullWidth value={data?.lunaStoreRef} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Référence Bearcod
            </Typography>
            <TextField disabled fullWidth value={data?.bearcodName} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Type Boutique
            </Typography>
            <TextField disabled fullWidth value={data?.storeType} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Entreprise Légal
            </Typography>
            <TextField disabled fullWidth value={currentEntity ?? 'Inconnue'} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              SIRET
            </Typography>
            <TextField disabled fullWidth value={data?.siret} />

            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Status
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={data?.isOnline} disabled />}
              label="Boutique en ligne"
            />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Date d'ouverture
            </Typography>
            <TextField
              disabled
              fullWidth
              value={
                data?.openingDate ? DateTime.fromISO(data?.openingDate).toLocaleString() : '  --  '
              }
            />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Facteur de quantité (pour l'ouverture)
            </Typography>
            <TextField disabled fullWidth value={data?.quantityFactor} />
            <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
              Taux de croissance
            </Typography>
            <TextField disabled fullWidth value={data?.growthFactor} />
          </LoadingPaper>
        </TabPanel>

        <DivTabPanel value={tab} index="stocks" style={{ marginTop: 0 }}>
          {selectedProduct === undefined && (
            <>
              <div style={{ display: 'flex' }}>
                <StockCard
                  mr
                  flex
                  title="NB D'ARTICLES TOTAL EN STOCK"
                  content={stock.data?.metadata.articleCount}
                />
                <StockCard
                  mr
                  flex
                  title="NB D'ARTICLES ANTI GASPI EN STOCK"
                  content={stock.data?.metadata.antiGaspiArticleCount}
                />

                <StockCard
                  flex
                  title="NB D'ARTICLES EXPIRES EN STOCK"
                  content={stock.data?.metadata.expiredArticleCount}
                />
              </div>
              <div style={{ padding: 10 }} />
              <div style={{ display: 'flex' }}>
                <StockCard
                  mr
                  flex
                  title="VALORISATION TOTALE DES ARTICLES EN STOCK"
                  content={priceToString(stock.data?.metadata.totalStockValue ?? 0)}
                />
                <StockCard
                  mr
                  flex
                  title="VALORISATION DES ARTICLES ANTI GASPI EN STOCK"
                  content={priceToString(stock.data?.metadata.totalAntiGaspiStockValue ?? 0)}
                />

                <StockCard
                  flex
                  title="VALORISATION TOTALE DES ARTICLES EXPIRES EN STOCK"
                  content={priceToString(stock.data?.metadata.totalExpiredStockValue ?? 0)}
                />
              </div>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  paddingTop: '16px',
                  width: '100%',
                }}
              >
                {tab === 'details' && selectedProduct === undefined ? (
                  <>
                    {isStockAdmin ? (
                      <ConfirmButton
                        buttonText="Supprimer les articles"
                        buttonProps={{
                          disabled: cleanStoreArticlesMut.isPending,
                          sx: {
                            marginRight: '16px',
                            height: '42px',
                            ...ButtonOverrides.redOutlined,
                          },
                          variant: 'outlined',
                        }}
                        description="Etes-vous sur de vouloir supprimer tous les articles en stock de cette boutique ? Cette action est irréversible et bloquera toutes les ventes jusqu'au prochain inventaire"
                        onConfirm={() => cleanStoreArticlesMut.mutateAsync(storeId)}
                        title="Supprimer tous les articles ?"
                        confirmButton={
                          cleanStoreArticlesMut.isPending
                            ? {
                                loading: true,
                                text: 'Suppression en cours     ',
                              }
                            : { text: 'Supprimer' }
                        }
                      />
                    ) : null}

                    {isStockAdmin ? (
                      <Button
                        variant="contained"
                        onClick={() => {
                          setModalMode('moove')
                          setShowModal(true)
                        }}
                      >
                        Déplacer tous les articles
                      </Button>
                    ) : null}
                    <div style={{ padding: 10 }} />
                    {data?.storeType === 'BOUTIQUE' ||
                    data?.storeType === 'SHIPPING' ||
                    data?.storeType === 'WAREHOUSE' ? (
                      <Button
                        variant="contained"
                        onClick={() => {
                          setModalMode('command')
                          setShowModal(true)
                        }}
                        disabled={isLoadingBdl || isLoading}
                      >
                        <Download />
                        {data?.storeType === 'BOUTIQUE' || data?.storeType === 'WAREHOUSE'
                          ? 'Générer un bon de commande'
                          : 'Générer un bon de livraison'}
                      </Button>
                    ) : null}
                    <div style={{ padding: 10 }} />
                    <StoreCSVExportButton onlySelected selectedStores={[storeId]} />
                    <div style={{ padding: 10 }} />
                    {(data?.storeType === 'BOUTIQUE' || data?.storeType === 'WAREHOUSE') && (
                      <StoreRestockCSVExportButton selectedStore={storeId} />
                    )}
                    <div style={{ padding: 10 }} />
                    {data?.storeType === 'BOUTIQUE' || data?.storeType === 'WAREHOUSE' ? (
                      <Button
                        variant="contained"
                        onClick={handleDownloadAuditFile}
                        disabled={loadingAudit}
                      >
                        <Download />
                        Export audit
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
              <Paper
                sx={{
                  marginTop: '16px',
                }}
              >
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    padding: '16px',
                  }}
                >
                  <TextField
                    variant="outlined"
                    label="Recherche"
                    value={stockSearch}
                    onChange={(e) => setStockSearch(e.target.value)}
                  />
                  <div style={{ flexGrow: 1 }} />
                </div>
                <SimpleDataGrid
                  columns={stockColDef}
                  rows={stockRows}
                  loading={stock.isLoading}
                  checkboxSelection={false}
                  sx={DataGridOverrides.wrapHeaderNames}
                  onRowClick={(row) => setSelectedProduct(row.id as number)}
                />
              </Paper>
            </>
          )}
          {selectedProduct !== undefined && (
            <Paper
              sx={{
                marginTop: '16px',
              }}
            >
              <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
                <Select
                  value={articleState}
                  onChange={(e) => setArticleState(e.target.value as FilterArticleState)}
                >
                  <MenuItem value="all">Tout</MenuItem>
                  <MenuItem value="antigaspi">Anti-gaspi</MenuItem>
                  <MenuItem value="expired">Expiré</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                </Select>
                <div style={{ flexGrow: 1 }} />
              </div>
              <SimpleDataGrid
                columns={articlesColDef}
                rows={articlesRows}
                getRowId={(row) => row.id}
                loading={articles.isLoading}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) =>
                  setSelectedArticles(newSelection as number[])
                }
                sx={DataGridOverrides.wrapHeaderNames}
              />
            </Paper>
          )}
        </DivTabPanel>
        <TabPanel value={tab} index="inventory">
          <PendingInventoryTab
            shopId={storeId}
            onInventoryDataChange={setPendingInventoryData}
            setTab={handleChange}
          />
        </TabPanel>
        <TabPanel value={tab} index="inventories">
          <PastInventoriesTab shopId={storeId} />
        </TabPanel>
        <TabPanel value={tab} index="hours">
          <OpeningHoursConfigPanel
            storeId={storeId}
            openingConfigs={openingConfigs}
            onDayConfigChange={handleDayConfigChange}
          />
        </TabPanel>
        <TabPanel value={tab} index="kiosks">
          <KiosksManagementPanel storeReference={data?.checkpointStoreRef || ''} />
        </TabPanel>
        <DivTabPanel value={tab} index="stats">
          <InventoriesStatsTab shopId={storeId} />
        </DivTabPanel>
      </Page>
    </>
  )
}
